import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import './styles/VocabularyReview.css';
import { 
  PlayIcon, 
  PauseIcon, 
  ArrowPathIcon, 
  CheckIcon, 
  XMarkIcon, 
  StarIcon, 
  LightBulbIcon,
  TrophyIcon,
  BookOpenIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';

const VocabularyReview = () => {
  const [vocabularyList, setVocabularyList] = useState([]);
  const [currentWords, setCurrentWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [reviewComplete, setReviewComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch vocabulary list
  useEffect(() => {
    fetchVocabularyList();
  }, []);

  const fetchVocabularyList = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/list?page=1&per_page=999`);
      // Extract list_word from response
      const words = response.data.list_word || [];
      setVocabularyList(words);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching vocabulary:', err);
      setError('Không thể tải danh sách từ vựng');
      setLoading(false);
    }
  };

  // Start new review session
  const startReview = () => {
    if (vocabularyList.length < 10) {
      setError('Cần ít nhất 10 từ vựng để bắt đầu ôn tập');
      return;
    }

    // Randomly select 10 words
    const shuffled = [...vocabularyList].sort(() => 0.5 - Math.random());
    const selectedWords = shuffled.slice(0, 10);
    
    setCurrentWords(selectedWords);
    setCurrentIndex(0);
    setScore(0);
    setTotalQuestions(10);
    setReviewComplete(false);
    setShowAnswer(false);
    setIsPlaying(false);
    setError('');
  };

  // Next word
  const nextWord = () => {
    if (currentIndex < currentWords.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowAnswer(false);
    } else {
      setReviewComplete(true);
    }
  };

  // Previous word
  const previousWord = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowAnswer(false);
    }
  };

  // Toggle play/pause
  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  // Mark as known/unknown
  const markWord = (isKnown) => {
    if (isKnown) {
      setScore(score + 1);
    }
    nextWord();
  };

  // Get current word
  const currentWord = currentWords[currentIndex];

  if (loading) {
    return (
      <div className="vocabulary-review-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Đang tải từ vựng...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vocabulary-review-container">
        <div className="error-message">
          <XMarkIcon className="error-icon" />
          <h3>Lỗi</h3>
          <p>{error}</p>
          <button onClick={fetchVocabularyList} className="retry-btn">
            <ArrowPathIcon className="w-4 h-4" /> Thử lại
          </button>
        </div>
      </div>
    );
  }

  if (reviewComplete) {
    const percentage = Math.round((score / totalQuestions) * 100);
    return (
      <div className="vocabulary-review-container">
        <div className="review-complete">
          <div className="completion-animation">
            <TrophyIcon className="trophy-icon" />
            <h2>Hoàn thành ôn tập!</h2>
          </div>
          
          <div className="score-display">
            <div className="score-circle">
              <span className="score-number">{score}</span>
              <span className="score-total">/{totalQuestions}</span>
            </div>
            <div className="score-percentage">{percentage}%</div>
          </div>

          <div className="score-message">
            {percentage >= 80 && <p className="excellent">Xuất sắc! Bạn đã nắm vững từ vựng này!</p>}
            {percentage >= 60 && percentage < 80 && <p className="good">Tốt! Hãy tiếp tục ôn tập để cải thiện!</p>}
            {percentage < 60 && <p className="need-improvement">Cần ôn tập thêm để ghi nhớ tốt hơn!</p>}
          </div>

          <div className="action-buttons">
            <button onClick={startReview} className="restart-btn">
              <ArrowPathIcon className="w-4 h-4" /> Ôn tập lại
            </button>
            <button onClick={() => window.location.reload()} className="new-session-btn">
              <StarIcon className="w-4 h-4" /> Phiên mới
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vocabulary-review-container">
      <div className="review-header">
        <h1>🎯 Ôn tập từ vựng</h1>
      </div>

      {currentWords.length === 0 ? (    
        <div className="start-review">
          <div className="start-card">
            <BookOpenIcon className="start-icon" />
            <h2>Bắt đầu ôn tập</h2>
            <p>Bạn có {vocabularyList.length} từ vựng để ôn tập</p>
            <button onClick={startReview} className="start-btn">
              <PlayIcon className="w-5 h-5" /> Bắt đầu
            </button>
          </div>
        </div>
      ) : (
        <div className="review-session">
          <div className="session-layout">
            {/* Left Sidebar - Compact Header */}
            <div className="left-sidebar">
              <div className="compact-header">
                <div className="progress-section">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{currentIndex + 1} / {totalQuestions}</span>
                </div>
                <div className="score-section">
                  <span className="score-text">Điểm: {score}</span>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="main-content">
              {/* Word Card */}
              <div className="word-card">
                <div className="word-header">
                  <h2 className="word-text">{currentWord.word}</h2>
                  {currentWord.kanji && (
                    <div className="kanji-text">{currentWord.kanji}</div>
                  )}
                </div>

                <div className="word-content">
                  {!showAnswer ? (
                    <div className="question-section">
                      <QuestionMarkCircleIcon className="question-icon" />
                      <p className="question-text">Bạn có nhớ nghĩa của từ này không?</p>
                      <button 
                        onClick={() => setShowAnswer(true)} 
                        className="show-answer-btn"
                      >
                        <LightBulbIcon className="w-4 h-4" /> Xem đáp án
                      </button>
                    </div>
                  ) : (
                    <div className="answer-section">
                      <div className="meaning">
                        <h3>Ý nghĩa:</h3>
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          components={{
                            p: ({children}) => <p className="meaning-content">{children}</p>
                          }}
                        >
                          {currentWord.meaning}
                        </ReactMarkdown>
                      </div>
                      
                      {currentWord.usage && (
                        <div className="usage">
                          <h3>Cách dùng:</h3>
                          <ReactMarkdown 
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw]}
                            components={{
                              p: ({children}) => <p className="usage-content">{children}</p>
                            }}
                          >
                            {currentWord.usage}
                          </ReactMarkdown>
                        </div>
                      )}
                      
                      {currentWord.example && (
                        <div className="example">
                          <h3>Ví dụ:</h3>
                          <ReactMarkdown 
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw]}
                            components={{
                              p: ({children}) => <p className="example-content">{children}</p>
                            }}
                          >
                            {currentWord.example}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                  <button 
                    onClick={() => markWord(false)} 
                    className="unknown-btn"
                    disabled={!showAnswer}
                  >
                    <XMarkIcon className="w-4 h-4" /> Không nhớ
                  </button>
                  
                  <button 
                    onClick={() => markWord(true)} 
                    className="known-btn"
                    disabled={!showAnswer}
                  >
                    <CheckIcon className="w-4 h-4" /> Nhớ rõ
                  </button>
                </div>
              </div>

              {/* Navigation Controls */}
              <div className="navigation-controls">
                <button 
                  onClick={previousWord} 
                  className="nav-btn prev-btn"
                  disabled={currentIndex === 0}
                >
                  ← Trước
                </button>
                
                <button 
                  onClick={togglePlay} 
                  className={`play-btn ${isPlaying ? 'paused' : 'playing'}`}
                >
                  {isPlaying ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
                  {isPlaying ? ' Tạm dừng' : ' Tiếp tục'}
                </button>
                
                <button 
                  onClick={nextWord} 
                  className="nav-btn next-btn"
                  disabled={currentIndex === currentWords.length - 1}
                >
                  Tiếp →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VocabularyReview;
